import ChecklistBusiness from "@/business/crud/ChecklistBusiness.js";
import ListaBusiness from "@/business/crud/ListaBusiness.js";
import FrontBusiness from "@/business/FrontBusiness.js";
import IndicadorPessoalBusiness from "@/business/indicadores/IndicadorPessoalBusiness.js";
import IndicadorRealBusiness from "@/business/indicadores/IndicadorRealBusiness.js";
import ExigenciaBusiness from "@/business/livros/ExigenciaBusiness.js";
import Utils from "@/commons/Utils.js";
import RegrasEstaduaisRjBusiness from "@/business/estados/RegrasEstaduaisRjBusiness.js";

const cacheChecklists = {};

export default {

  INDICE_EXIGENCIA: '__EXIGENCIA',

  /**
   * Chaves de `indices` que são controle interno, não resposta de requisito. Telas que listam os
   * índices crus (auditoria, comparação do Odin) precisam pular todas.
   *
   * `__EXIGENCIA` guarda o mapa `indice -> texto editado à mão` das exigências. Não é resposta de
   * ninguém e nunca teve linha própria: quando a auditoria montava o checklist a partir de
   * `checklistProcessado`, ela iterava requisitos, então essa chave simplesmente não aparecia. A
   * lista compilada de exigências continua sendo exibida pelo formatter de `exigencias`.
   */
  INDICES_INTERNOS: Object.freeze(['__EXIGENCIA', 'checklistProcessado', 'respondidos', 'quantidade']),

  /**
   * Texto de exibição de uma resposta crua de `indices`.
   *
   * Resposta nem sempre é escalar: campo múltiplo e VINCULO guardam array, requisito repetidor
   * (GROUP) guarda lista de objetos. As telas de auditoria faziam `String(valor)` direto, o que
   * imprimia `[object Object]` nesses casos.
   */
  descreverResposta(valor){

    if (valor === null || valor === undefined || valor === '') {
      return valor;
    }

    if (Array.isArray(valor)) {
      return valor.map(item => this.descreverResposta(item))
        .filter(texto => texto !== null && texto !== undefined && texto !== '')
        .join(', ');
    }

    if (typeof valor === 'object') {
      return Object.entries(valor)
        .map(([chave, item]) => {
          const texto = this.descreverResposta(item);
          return texto === null || texto === undefined || texto === '' ? null : `${chave}: ${texto}`;
        })
        .filter(linha => linha)
        .join('; ');
    }

    return String(valor).trim();
  },

  MODO: Object.freeze({
    STORAGE: 'storage',
    VIEW: 'view'
  }),

  init: Utils.debounce(async () => {

    let p2;

    if(!window.listas?.length){
      p2 = ListaBusiness.getAll().then(l => window.listas = l);
    }

    await Promise.all([p2]);

  }),

  /**
   * De onde vem a estrutura de cada tela: a fonte do vínculo e o escopo que o backend usa.
   *
   * `custa-adicional` e `siscoaf` não têm vínculo de checklist. Ficam de fora de propósito —
   * `protocolo_custa_adicional.checklist` nunca foi populado da configuração (não existe
   * regerarChecklists para ela), então a tela sempre exibiu vazio. Resolver por tipo de serviço
   * faria aparecer um checklist onde nunca apareceu.
   */
  FONTES: Object.freeze({
    protocolo:    {fonte: 'tipoServico',   escopo: 'PROTOCOLO'},
    ato:          {fonte: 'tipoServico',   escopo: 'ATO'},
    envolvidos:   {fonte: 'tipoServico',   escopo: 'ATO'},
    certidao:     {fonte: 'tipoServico',   escopo: 'CERTIDAO'},
    oficio:       {fonte: 'tipoServico',   escopo: 'TODOS'},
    ocorrencia:   {fonte: 'tipoServico',   escopo: 'TODOS'},
    notificacao:  {fonte: 'tipoServico',   escopo: 'TODOS'},
    documento:    {fonte: 'tipoDocumento'},
    etapa:        {fonte: 'etapa'},
  }),

  /**
   * Estrutura de checklist da entidade, lida da CONFIGURAÇÃO.
   *
   * Antes lia `dto.checklist`, o snapshot que era copiado para dentro de cada registro. O snapshot
   * saiu porque pesava (746 MB de coluna num único protocolo de 1.459 atos) e porque nunca
   * congelou de verdade: fragmentos já eram resolvidos pela configuração viva aqui mesmo, trocar o
   * tipo de serviço o sobrescrevia e quatro endpoints de regerar o reescreviam sem olhar status.
   *
   * O filtro por tipo é o mesmo de sempre, inclusive o `default` que exclui PROTOCOLO das telas de
   * certidão, ofício, ocorrência, notificação e documento — é ele que compensa a ausência de
   * filtro do lado do backend nessas entidades.
   */
  async getChecklists(dto, tipo){

    let checklists = await this.buscarConfiguracao(dto, tipo) || [];

    switch ((tipo || '').toLowerCase()){
      case 'protocolo':
        checklists = checklists.filter(c => c.tipoChecklist === 'PROTOCOLO');
        break;
      case 'ato':
        checklists = checklists.filter(c => c.tipoChecklist === 'ATO');
        break;
      case 'envolvidos':
        checklists = checklists.filter(c => c.tipoChecklist === 'ENVOLVIDOS');
        break;
      case 'etapa':
        checklists = checklists.filter(c => c.tipoChecklist === 'ETAPA');
        break;
      default:
        checklists = checklists.filter(c => c.tipoChecklist !== 'PROTOCOLO');
        break;
    }

    return Utils.orderBy(checklists, 'ordem').map(c => ({
      id: c.checklistId,
      nome: c.titulo || c.nome,
      requisitos: c.requisitos,
    }));
  },

  /**
   * Contagem de respondidos por grupo de checklist do ato, para a SIMULAÇÃO de registro/lavratura.
   *
   * Substitui a leitura do snapshot `ato.checklist`, e conta **cada grupo contra o `indices` onde
   * as respostas dele realmente moram**:
   *
   * - grupo de tipo `ATO` → `ato.indices`;
   * - grupo de tipo `ENVOLVIDOS` → uma contagem **por envolvido**, cada uma contra o
   *   `envolvido.indices`, que é onde `Editor.vue.gerarDto` as grava.
   *
   * O snapshot misturava os dois: trazia os grupos de ENVOLVIDOS junto e a simulação os contava
   * contra o `indices` do ato, onde essas respostas nunca estiveram. Todo requisito de envolvido
   * aparecia como não respondido, então o registro era barrado mesmo com tudo preenchido, e o erro
   * nomeava o checklist sem dizer de qual envolvido era. Agora só acusa quem está de fato
   * incompleto, e diz o nome.
   */
  async contarChecklistsDoAto(ato, vinculos = {}){

    const checklists = await this.buscarConfiguracao(ato, 'ato') || [];
    const contagens = [];

    if (!vinculos.matricula && RegrasEstaduaisRjBusiness.dispensarImovelChecklist(ato?.tipoServico?.tipoTribunal)) {
      vinculos = { ...vinculos, dispensarImovel: true };
    }

    for (const config of Utils.orderBy(checklists, 'ordem')) {

      const grupo = {
        requisitos: config.requisitos,
        id: config.checklistId,
        titulo: config.titulo || config.nome,
      };

      if (config.tipoChecklist === 'ENVOLVIDOS') {
        for (const envolvido of (ato?.envolvidos || [])) {
          const ctrl = await this.tratarGrupoChecklist(grupo, {...envolvido?.indices},
            {...vinculos, envolvidos: [envolvido]});
          contagens.push({
            id: grupo.id,
            titulo: `${grupo.titulo} (${this.nomearEnvolvido(envolvido)})`,
            contador: ctrl.contador,
          });
        }
        continue;
      }

      const ctrl = await this.tratarGrupoChecklist(grupo, {...ato?.indices}, vinculos);
      contagens.push({id: grupo.id, titulo: grupo.titulo, contador: ctrl.contador});

    }

    return contagens;
  },

  /** Nome do envolvido para a mensagem de checklist incompleto. */
  nomearEnvolvido(envolvido){
    return envolvido?.nome
      || envolvido?.indicadorPessoalVersao?.nome
      || 'envolvido sem nome';
  },

  /**
   * Consulta o resolver. Devolve `null` quando a tela não tem fonte de vínculo (custa adicional,
   * siscoaf) ou quando o dto ainda não tem o tipo escolhido — nos dois casos não há checklist a
   * exibir.
   */
  async buscarConfiguracao(dto, tipo){

    const origem = this.FONTES[(tipo || '').toLowerCase()];
    if(!origem || !dto){
      return null;
    }

    switch (origem.fonte){
      case 'tipoServico':
        return dto.tipoServico?.id
          ? ChecklistBusiness.resolverPorTipoServico(dto.tipoServico.id, origem.escopo)
          : null;
      case 'tipoDocumento':
        return dto.tipoDocumento?.id
          ? ChecklistBusiness.resolverPorTipoDocumento(dto.tipoDocumento.id)
          : null;
      case 'etapa':
        return dto.etapa?.id
          ? ChecklistBusiness.resolverPorEtapa(dto.etapa.id)
          : null;
      default:
        return null;
    }
  },

  async processarEntidade(dto, checklists, tipo, documentos = [], modo = 'storage'){

    let vinculos = {};
    if (dto?.envolvidos) {
      vinculos.envolvidos = dto?.envolvidos;
    }

    if (dto?.indicadorReal) {
      vinculos.matricula = dto?.indicadorReal;
    } else if (dto?.indicadorRealVinculado) {
      vinculos.matricula = dto?.indicadorRealVinculado;
    }

    if (dto?.registroAuxiliar) {
      vinculos.registroAuxiliar = dto?.registroAuxiliar;
    }

    if (documentos?.length) {
      vinculos.documentos = Object.freeze(documentos);
    }

    return await this.gerarChecklist(checklists, dto?.indices, vinculos, modo);

  },

  /**
   * Mapa de respostas inicial de um reprocessamento.
   *
   * `indices` é sempre reconstruído do zero: só entra nele o que a travessia da configuração
   * vigente reproduzir. As exigências reescritas à mão (`__EXIGENCIA`) não podem seguir essa
   * regra — elas dependem da resposta atual, e a exigência que deixou de ser disparada não é
   * revisitada. Sem carregar o mapa adiante, trocar a resposta e voltar atrás (ou rodar o
   * "Recontar Checklist", que reprocessa e salva protocolo, atos e documentos de uma vez)
   * descartava o texto do cartório em definitivo, porque o backend substitui `indices` inteiro,
   * sem merge. O texto editado só sai por "Usar Texto Original".
   */
  iniciarIndices(respostas = {}){
    const editadas = respostas?.[this.INDICE_EXIGENCIA];
    return editadas ? {[this.INDICE_EXIGENCIA]: {...editadas}} : {};
  },

  async gerarChecklist(checklists = [], respostas = {}, vinculos = {}, modo = 'view'){

    let base = {
      exigencias: [],
      indicesExigencia: {},
      indices: this.iniciarIndices(respostas),
      // acompanha `indices`: os grupos compartilham o mesmo escopo de respostas, então um índice
      // repetido entre dois checklists do mesmo item também colide
      indicesDeclarados: {},
      grupos: [],
      contador: {quantidade : 0, respondidos : 0}
    };

    for (let grupo of checklists) {

      let ctrl = {
        exigencias: base.exigencias,
        indicesExigencia: base.indicesExigencia,
        indices: base.indices,
        indicesDeclarados: base.indicesDeclarados,
        requisitos: [],
        contador: {quantidade : 0, respondidos : 0},
        id: grupo.id,
        recursividade: [grupo.id]
      };

      await this.tratarGrupoChecklist(grupo, respostas, vinculos, ctrl, modo);

      base.grupos.push({id: grupo.id, nome: grupo.nome, requisitos: ctrl.requisitos.map(r => {
        // if(modo == this.MODO.STORAGE){
        //   return {
        //    TODO tratar pra nao salvar o checklist completo
        //   }
        // }
        return r;
        }), contador: ctrl.contador});

      base.contador.quantidade += ctrl.contador.quantidade;
      base.contador.respondidos += ctrl.contador.respondidos;

    }

    return base;

  },

  async tratarGrupoChecklist(grupo, respostas = {}, vinculos = {}, ctrl, modo = 'view' ){

    if(!ctrl){
      ctrl = {
        exigencias : [],
        indicesExigencia : {},
        indices : this.iniciarIndices(respostas),
        requisitos : [],
        contador : { quantidade : 0, respondidos : 0},
        nome: grupo.nome,
        titulo: grupo.titulo,
        id: grupo.id,
        recursividade: [grupo.id]
      };
    }

    for (let requisito of grupo.requisitos) {
      await this.tratarRequisito({...requisito}, ctrl, respostas, vinculos, 1, undefined,  modo);
    }

    return ctrl;

  },

  criarRequisito(requisito, resposta, modo = 'view'){

    let req = Utils.clone(requisito);
    req.condicoes = (req?.condicoes || []).map(r => r.valor);
    req.resposta = resposta;

    if(!req.temExigencia){
      delete req.temExigencia;
    }

    if(!req.condicoes?.length){
      delete req.condicoes;
    }

    if(modo == this.MODO.STORAGE){

      delete req.ajuda;
      delete req.multiplicador
      if(!req.sublist?.length){
        delete req.sublist;
      }
    }

    return req;

  },

  async tratarRequisito(requisito, ctrl, respostas = {}, vinculos = {}, n = 1, prefix = '', modo = 'view'){

    if (++n > 20 || (modo == this.MODO.STORAGE && requisito.tipo == 'LABEL')) {
      return;
    }

    if (requisito.tipo == 'CHECKLIST') {
      if(ctrl.recursividade.includes(requisito.checklist)){
        return;
      }

      ctrl.recursividade.push(requisito.checklist);

      let c = cacheChecklists?.[requisito.checklist] || [];
      if(!c?.length && requisito.checklist){
        c = await ChecklistBusiness.getByIdCached(requisito.checklist).then(e => e?.checklist || []).catch(e => []);
        cacheChecklists[requisito.checklist] = c;
      }

      for (let req of c) {
        await this.tratarRequisito({...req}, ctrl, respostas, vinculos, n, prefix, modo);
      }
      return;

    }

    if (!requisito.indice) {
      requisito.semIndice = true;
      //adiciona na lista de itens sem indice
      ctrl.requisitos.push(this.criarRequisito(requisito, undefined, modo));
      return;
    }

    // Índice duplicado: dois requisitos DIFERENTES gravando na mesma chave de resposta. Um
    // sobrescreve o outro em silêncio, e a minuta que lê $(checklist.<indice>) pega o que sobrar.
    //
    // A detecção ficou anos comentada aqui como `ctrl.indices[indice] != undefined`, que acusava
    // qualquer índice já visto. Isso dava falso positivo no caso legítimo de o MESMO requisito ser
    // alcançado duas vezes — dois checklists do mesmo ato incluindo o mesmo fragmento, por
    // exemplo, em que compartilhar a resposta é o comportamento esperado. Comparar a assinatura
    // (tipo + pergunta) separa os dois casos.
    //
    // Repetidor e condição não entram na conta: GROUP traverse com um ctrl próprio
    // (ctrlInterno.indices aponta para o escopo da instância) e só as condições ativas são
    // percorridas, então nenhum dos dois reaproveita o escopo de índices do pai.
    ctrl.indicesDeclarados = ctrl.indicesDeclarados || {};
    const assinatura = `${requisito.tipo}|${requisito.pergunta || ''}`;
    if (ctrl.indicesDeclarados[requisito.indice] === undefined) {
      ctrl.indicesDeclarados[requisito.indice] = assinatura;
    } else if (ctrl.indicesDeclarados[requisito.indice] !== assinatura) {
      requisito.indiceDuplicado = true;
    }

    // CAPTURA RESPOSTA ANTERIOR DO INDICE
    if (requisito.tipo != 'AUTOMATICO' && respostas?.[requisito.indice]) {
      ctrl.indices[requisito.indice] = respostas[requisito.indice];
    } else if (requisito.tipo == 'AUTOMATICO') {

      try {
        await this.automatizarValor(requisito, ctrl, respostas, vinculos);
      } catch (e) {
        console.error('erro ao tentar prencher resposta', e)
      }

    }

    if(ctrl.indices[requisito.indice] === undefined){
      ctrl.indices[requisito.indice] = null;
    }

    if (!['GROUP', 'LABEL'].includes(requisito.tipo)) {

      ++ctrl.contador.quantidade;

      let val = ctrl.indices[requisito.indice];

      if (Array.isArray(val) || requisito.apresentacao == 'checkbox' || requisito.multiplo) {
        val = val?.length;
      }

      if (val) {
        ++ctrl.contador.respondidos;
      }

    }

    if (requisito.tipo == 'GROUP') {
      await this.tratarRepetidores({...requisito}, ctrl, respostas, vinculos, n, modo);
    } else if (requisito.condicoes?.length) {
      await this.tratarSubrequisitos({...requisito}, ctrl, respostas, vinculos, n, modo);
    } else {
      requisito.uuidReq = `req-${prefix}${prefix ? '-':''}${requisito.indice.replace(' ', '_')}`;

      requisito = this.criarRequisito(requisito, ctrl.indices[requisito.indice], modo);

      if(requisito.tipo == 'EXIGENCIA' && ctrl.indices[requisito.indice]){

        const exigencia = {
          // requisito: requisito,
          indice: requisito.indice,
          // indices: ctrl.indices,
          // envolvido: vinculos?.envolvido,
          exigencia: Utils.clone(ctrl.indices[requisito.indice])
        }

        ctrl.exigencias.push(exigencia);
        requisito.temExigencia = true;
        requisito.ctrlExigencias = [exigencia];

      }

      ctrl.requisitos.push(requisito);

    }

  },

  async automatizarValor(requisito, ctrl, respostas = {}, vinculos = {}){
    requisito.readOnly = true;
    switch (requisito.chave) {
      case 'TIPO_PESSOA':
        if (vinculos.envolvido) {
          ctrl.indices[requisito.indice] = vinculos.envolvido.tipoPessoa == 'JURIDICA' ? 'Pessoa jurídica' : 'Pessoa física';
        }
        break;
      case 'INDIVIDUO_MENOR':
        if (vinculos.envolvido && vinculos.envolvido.tipoPessoa != 'JURIDICA') {
          ctrl.indices[requisito.indice] = vinculos.envolvido.menor ? 'Sim' : 'Não';
        }
        break;
      case 'INDIVIDUO_INCAPAZ':
        if (vinculos.envolvido && vinculos.envolvido.tipoPessoa != 'JURIDICA') {
          ctrl.indices[requisito.indice] = vinculos.envolvido.incapaz ? 'Sim' : 'Não';
        }
        break;
      case 'INDIVIDUO_FALECIDO':
        if (vinculos.envolvido && vinculos.envolvido.tipoPessoa != 'JURIDICA') {
          ctrl.indices[requisito.indice] = vinculos.envolvido.falecido ? 'Sim' : 'Não';
        }
        break;
      case 'ESTADO_CIVIL':
        if (vinculos.envolvido && vinculos.envolvido.tipoPessoa != 'JURIDICA'){
          let valor;
          if(vinculos.envolvido.estadoCivil != 'CASADO' && vinculos.envolvido.possuiUniaoEstavel){
            valor = 'União Estável';
          }else{
            valor = FrontBusiness.getLabel(vinculos.envolvido.estadoCivil, IndicadorPessoalBusiness.getEstadosCivil())?.replace('o(a)', 'o')
          }
          ctrl.indices[requisito.indice] = valor || 'Não Informado';
        }
        break;
      case 'CASAMENTO_APOS_LEI_6515':
        if (vinculos.envolvido && vinculos.envolvido.tipoPessoa != 'JURIDICA') {
          ctrl.indices[requisito.indice] = vinculos.envolvido.casamentoAposLei6515 ? 'Sim' : 'Não';
        }
        break;
      case 'REGIME_CASAMENTO':
        if (vinculos.envolvido && vinculos.envolvido.tipoPessoa != 'JURIDICA'){
          let valor;
          if(vinculos.envolvido.regimeCasamento){
            valor = FrontBusiness.getLabel(vinculos.envolvido.regimeCasamento, IndicadorPessoalBusiness.getRegimes())
            let condicoes = (requisito.condicoes || []).map(e => e.valor);
            if (valor && !condicoes.includes(valor)) {
              valor = FrontBusiness.getLabel(vinculos.envolvido.regimeCasamento, IndicadorPessoalBusiness.getRegimesOld())
            }
          }
          ctrl.indices[requisito.indice] = valor;
        }
        break;
      case 'LIVRO':
        if (vinculos.matricula) {
          ctrl.indices[requisito.indice] = vinculos.matricula.livro ||  'MATRICULA';
        }
        if (vinculos.registroAuxiliar) {
          ctrl.indices[requisito.indice] = 'REGISTRO_AUXILIAR';
        } else if (vinculos.dispensarImovel) {
          ctrl.indices[requisito.indice] = '-';
        }
        break;
      case 'CATEGORIA_IMOVEL':
        if (vinculos.matricula) {
          ctrl.indices[requisito.indice] = Utils.capitalize(vinculos.matricula.categoria?.toLowerCase() || '');
        } else if (vinculos.dispensarImovel) {
          ctrl.indices[requisito.indice] = '-';
        }
        break;
      case 'UNIDADE_AUTONOMA':
        if (vinculos.matricula) {
          ctrl.indices[requisito.indice] = vinculos.matricula.unidadeAutonoma != undefined ? vinculos.matricula.unidadeAutonoma ? 'Sim' : 'Não' : null;
        } else if (vinculos.dispensarImovel) {
          ctrl.indices[requisito.indice] = '-';
        }
        break;
      case 'POSSUI_DEVEDOR':
        if(vinculos.envolvidos){
          ctrl.indices[requisito.indice] = vinculos.envolvidos.filter(e => e.papel == 'DEVEDOR').length ? 'Sim' : 'Não';
        }
        break;
      case 'PAPEL':
        if (vinculos.envolvido) {
          ctrl.indices[requisito.indice] = IndicadorRealBusiness.getCachedPapeis().find( e => e.id === vinculos.envolvido?.papel)?.nome;
        }
        break;
    }

  },

  async tratarSubrequisitos(requisito, ctrl, respostas = {}, vinculos = {}, n = 1, modo = 'view'){

    let exigencias = [];
    let resposta = ctrl.indices[requisito.indice];
    let respostaLocal = Utils.clone(resposta);
    let valores = requisito.condicoes.map(v => v.valor);

    if (requisito.tipo === 'VINCULO' && requisito.vinculo === 'TITULO') {

      let documentos = vinculos.documentos || [];
      let tipos = requisito.condicoes.map(v => v.id);

      // console.group('tratarSubrequisitos');
      // console.debug('indice', Utils.clone(requisito.indice));
      // console.debug('respostaLocal', Utils.clone(respostaLocal));
      // console.debug('resposta', Utils.clone(resposta));
      // console.debug('valores', Utils.clone(valores));
      // // console.debug('tipos', tipos);
      // console.debug('documentos', Utils.clone(documentos));
      // console.groupEnd();

      let docs = documentos.filter(d => tipos.includes(d.tipoId)).map(d => d.id);

      if (Array.isArray(resposta)) {
        // console.debug('docs', Utils.clone(docs));
        // console.debug('resposta', Utils.clone(resposta));
        // console.debug('valores', Utils.clone(valores));
        // console.debug('documentos', Utils.clone(documentos));
        resposta = resposta.filter(i => docs.includes(i));
        respostaLocal = documentos.filter(d => resposta.includes(d.id)).map(d => d.tipo);
      } else if(!docs.includes(resposta)){
          resposta = null;
          respostaLocal = null;
      }

    }else{

      if (!Array.isArray(resposta) && !valores.includes(resposta)) {
        resposta = null;
      }

      if (Array.isArray(respostaLocal)) {
        resposta = [];
        respostaLocal.forEach((r, i) => {
          if (valores.includes(r)) resposta.push(ctrl.indices[requisito.indice][i])
        })
      }

    }

    ctrl.indices[requisito.indice] = resposta;

    let pularExigencia = false;
    requisito.temExigencia = false;

    const base = {
      exigencia: respostas?.[this.INDICE_EXIGENCIA]?.[requisito.indice] || requisito.exigencia,
      original: requisito.exigencia,
      indice: requisito.indice,
      // requisito: requisito,
      indices: ctrl.indices,
      envolvido: vinculos?.envolvido
    }

    ctrl.indices[this.INDICE_EXIGENCIA] = ctrl.indices[this.INDICE_EXIGENCIA] || {};
    if(respostas?.[this.INDICE_EXIGENCIA]?.[requisito.indice]){
      ctrl.indices[this.INDICE_EXIGENCIA][requisito.indice] = respostas?.[this.INDICE_EXIGENCIA]?.[requisito.indice];
    }

    if (requisito.tipo == 'SELECT' && requisito.apresentacao == 'checkbox') {

      pularExigencia = true;
      switch (requisito.tipoExigencia) {
        // nenhum item selecionado
        case 'vazio':
          if (requisito.exigencia && !resposta?.length) {
            exigencias.push({...base});
          }
          break;

        case 'incompleto':
          if (requisito.exigencia && resposta?.length != requisito.condicoes?.length) {
            exigencias.push({...base});
          }
          break;

        // itens nao selecionados geram exigencia
        case 'unchecked':
          (requisito.condicoes || []).filter(c => c.exigencia && !(resposta || []).includes(c.valor)).forEach(c => {
            const idx = `${base.indice}::${c.valor}`;

            const editada = respostas?.[this.INDICE_EXIGENCIA]?.[idx];
            if(editada){
              ctrl.indices[this.INDICE_EXIGENCIA][idx] = editada;
            }

            exigencias.push({...base, exigencia: editada || c.exigencia, original: c.exigencia, indice: idx});
          });
          break;

        default:
          pularExigencia = false;
          break;

      }

    }

    respostaLocal = Array.isArray(respostaLocal) ? respostaLocal : [respostaLocal];
    let l = (requisito.condicoes || []).filter(r => respostaLocal.includes(r.valor));

    if (!pularExigencia && respostaLocal.length) {
      l.filter(c => c.exigencia).forEach(c => {
        const idx = `${base.indice}::${c.valor}`;

        const editada = respostas?.[this.INDICE_EXIGENCIA]?.[idx];
        if(editada){
          ctrl.indices[this.INDICE_EXIGENCIA][idx] = editada;
        }
        // console.debug('--ex..', idx, editada)
        exigencias.push({...base, exigencia: editada || c.exigencia, original: c.exigencia, indice: idx});
      });
    }

    if (exigencias.length) {
      ctrl.exigencias.push(...exigencias);
      requisito.temExigencia = true;
      requisito.ctrlExigencias = exigencias;

      // console.debug(
      //   '--ex', this.INDICE_EXIGENCIA, requisito.indice,
      //   respostas?.[this.INDICE_EXIGENCIA],
      //   respostas
      // );

    }

    ctrl.requisitos.push(this.criarRequisito(requisito, ctrl.indices[requisito.indice], modo));

    for (let condicao of l) {
      for (let req of condicao.requisitos) {
        await this.tratarRequisito({...req}, ctrl, respostas, vinculos, n, undefined, modo);
      }
    }

  },

  limparObjetoEnvolvido(envolvido, simplificarVersao = false){
    const eCopy = Utils.clone(envolvido);

    delete eCopy.editandoInventariante;
    delete eCopy.indisponivel;
    delete eCopy.exibicao;
    delete eCopy.ocorrenciasAtivas;
    delete eCopy.inventarianteDocumentoInvalido;
    delete eCopy.ALERTA_DOCUMENTO;
    delete eCopy.contador;
    if(simplificarVersao){
      delete eCopy.id;
      eCopy.indicadorPessoalVersao = {id : eCopy.indicadorPessoalVersao.id};
    }
    if (JSON.stringify(eCopy.inventarianteVersao) === '{}') {
      eCopy.inventarianteVersao = null;
    }
    if (eCopy.categoriaEnvolvido === '') {
      eCopy.categoriaEnvolvido = null;
    }
    return eCopy;
  },

  async tratarRepetidores(requisito, ctrl, respostas = {}, vinculos = {}, n = 1, modo = 'view'){
    if (requisito.multiplicador == 'matricula') {
      requisito.multiplicador = 'manual';
    }

    if(ctrl?.repetindo && requisito.multiplicador != 'manual'){ //
      console.error("O checklist já está dentro de um requisito multiplicador");
      return;
    }

    if (!requisito.condicoes?.length) {
      console.error("Repetidor sem condições para repetir");
      return;
    }

    let sublist = [];

    switch (requisito.multiplicador) {
      case 'envolvido': {

        ctrl.indices.envolvidos = {...(ctrl.indices.envolvidos || {})};

        if(vinculos.envolvidos){
          sublist = vinculos.envolvidos.filter(e => requisito.papeis.includes(e.papel));
          sublist = Utils.orderBy(Utils.uniqBy(sublist, 'indicadorPessoalId'),  'ordem')
            .map(e => (this.limparObjetoEnvolvido({
              ...e,
              ...(e.indicadorPessoalVersao || {})
            })));

          sublist.forEach(envolvido => {
            ctrl.indices.envolvidos[envolvido.indicadorPessoalId] = {
              ...(ctrl.indices.envolvidos[envolvido.indicadorPessoalId] || {})
            };
          });
        }

        break;
      }

      case 'manual': {

        let list = ctrl.indices[requisito.indice] || [];
        if(!Array.isArray(list)){
          list = [];
        }

        ctrl.indices[requisito.indice] = list.map(e => {
          let r = e || {};
          delete r.requisitos;
          return r;
        });
        sublist = ctrl.indices[requisito.indice].map(e => ({}));

        break;
      }

      default:{
        console.error("Favor verificar a configuração do requisito: " + requisito.multiplicador);
        return;
      }

    }

    let i = -1;
    for (let grupo of sublist) {

      ++i;
      grupo.requisitos = [];

      let ctrlInterno = {
        exigencias: ctrl.exigencias,
        indicesExigencia: ctrl.indicesExigencia,
        indices: null,
        requisitos: grupo.requisitos,
        contador: ctrl.contador,
        repetindo: true,
        recursividade: [...ctrl.recursividade]
      };

      let respostasInternas = {}, prefix = undefined;

      switch (requisito.multiplicador) {
        case 'envolvido':
          ctrlInterno.indices = ctrl.indices.envolvidos[grupo.indicadorPessoalId];
          respostasInternas = respostas?.envolvidos?.[grupo.indicadorPessoalId] || {};
          prefix = grupo.indicadorPessoalId;
          vinculos.envolvido = grupo;
          break;
        default:
          ctrlInterno.indices = ctrl.indices[requisito.indice][i];
          respostasInternas = respostas?.[requisito.indice]?.[i] || {};
          prefix = i;
          break;
      }

      // if(requisito.indice == 'grupo_manual'){
      //   console.debug(i, sublist, respostasInternas, ctrlInterno.indices, ctrl.indices[requisito.indice], respostas[requisito.indice] );
      // }

      for (let condicao of requisito.condicoes) {
        for (let req of condicao.requisitos) {
          await this.tratarRequisito({...req}, ctrlInterno, respostasInternas, vinculos, n, prefix, modo);
        }
      }

      // if(requisito.indice == 'grupo_manual'){
      //   console.debug(i, sublist, respostasInternas, ctrlInterno.indices, ctrl.indices[requisito.indice], respostas[requisito.indice] );
      // }

      switch (requisito.multiplicador) {
        case 'envolvido':
          delete vinculos.envolvido;
          break;
        default:
          break;
      }

    }

    ctrl.requisitos.push(this.criarRequisito({...requisito, sublist}, null, modo));

  },

  async copilarExigencias(protocolo = {}, dto = {}, exigencias = [], apenasTexto = true) {

    let out = [], envolvidos = Utils.clone(dto?.envolvidos || []);

    let indices = {};
    if (dto?.ficha) {
      indices.matricula = Utils.clone(dto?.ficha);
    }

    if(dto?.tipoDocumento){
      indices.documento = {
        tipoDocumento : dto?.tipoDocumento?.nome,
        tituloDocumento: dto.nome
      }
    }

    for (let exigencia of exigencias) {
      let idx = {...indices, ...exigencia.indices};
      if(exigencia?.envolvido){
        idx.envolvido = {...exigencia?.envolvido}
      }
      let texto = await ExigenciaBusiness.copilarTextoExigencia(protocolo, exigencia.exigencia, idx, envolvidos);
      if(apenasTexto){
        out.push(texto);
      }else{
        let original = await ExigenciaBusiness.copilarTextoExigencia(protocolo, exigencia.original, idx, envolvidos);
        out.push({...exigencia, textoCopilado : texto, original});
      }
    }

    return out;
  },


  /*******************************/

  atualizarIndicesExigencias(exigencias, indices) {
    return exigencias.map(e => {
      e.indices = indices
      return e;
    });
  },

  async limparIndices(indices){

    // Retrato da árvore respondida que era gravado dentro de `indices` a cada salvamento. Saiu
    // porque a estrutura agora vem da configuração e ele era peso puro: uma cópia da árvore na
    // entidade e mais duas na auditoria (antes e depois), por edição, para sempre. Aqui é a
    // limpeza dos registros que ainda carregam a chave e passam por um novo salvamento.
    delete indices?.checklistProcessado;

    // Mapa vazio de exigências reescritas não precisa ir para o banco.
    if(indices?.[this.INDICE_EXIGENCIA] && !Object.keys(indices[this.INDICE_EXIGENCIA]).length){
      delete indices[this.INDICE_EXIGENCIA];
    }

    if(indices?.envolvidos?.length){
      Object.keys(indices.envolvidos).forEach(key => {
        const envolvido = indices.envolvidos[key]?.envolvido;
        if (envolvido && envolvido.id) {
          indices.envolvidos[key].envolvido = { id: envolvido.id };
        }
      });
    }

    return indices;
  }


}
